import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '~/libs/infrastructure/api/authentication';
import {
	getCzMaxPrice,
	getCzMaxPriceReferenceBasketVersions,
	postCzMaxPriceUpdate,
	postCzMaxPriceExportXls,
} from '~/libs/infrastructure/api/users/czMaxPrice';
import { CzMaxPrice, ProductUpdate } from '~/libs/infrastructure/schemas/czMaxPrice';
import ReferenceBasketVersion from '~/Api/PriceReferencing/ReferenceBasketVersion';
import { ExportResult, UserProductGroup } from '~/Api/User/ProductGroup';
import { Product as GeneralProduct } from '~/Product';
import { logGoogleTagManagerGroupExport, logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import { createEvidenceExport, createGroupExportXmlUrl } from '~/ProductGroup';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage';
import * as downloadFlashMessages from '~/ProductGroupDownloadPopover/DownloadFlashMessages';
import type { ThunkDispatch } from '~/Store';
import { Status } from '../components/ProductParametersPanel';
import downloadFileFromUrl from '~/utils/Download/downloadFileFromUrl';

const STALE_TIME = 5 * 60 * 1000;

function getCzMaxPriceQueryKey(groupId: string) {
	return ['czMaxPrice', groupId] as const;
}

function getCzMaxPriceReferenceBasketVersionsQueryKey() {
	return ['czMaxPrice', 'referenceBasketVersions'] as const;
}

interface ScheduledUpdate {
	packageSize: number | null;
	basketVersion: string;
	products: ProductUpdate[];
}

export function useCzMaxPrice(groupId: string) {
	const accessToken = useAccessToken();
	const userId = accessToken?.user.id;
	const queryClient = useQueryClient();
	const dispatch: ThunkDispatch<any> = useDispatch();
	const history = useHistory();

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const scheduledRef = useRef<ScheduledUpdate | null>(null);
	const [updateStatus, setUpdateStatus] = useState<Status | null>(null);

	const { data: czMaxPrice = null, isLoading: isFetching } = useQuery<CzMaxPrice>({
		queryKey: getCzMaxPriceQueryKey(groupId),
		queryFn: () => getCzMaxPrice(userId!, groupId),
		enabled: !!userId,
		staleTime: STALE_TIME,
	});
	const { data: basketVersions = null } = useQuery<ReferenceBasketVersion[]>({
		queryKey: getCzMaxPriceReferenceBasketVersionsQueryKey(),
		queryFn: getCzMaxPriceReferenceBasketVersions,
		staleTime: STALE_TIME,
	});

	const mutation = useMutation({
		mutationFn: (params: { packageSize: number | null; basketVersion: string; products: ProductUpdate[] }) =>
			postCzMaxPriceUpdate(userId!, groupId, params.packageSize, params.basketVersion, params.products),
		onMutate: () => setUpdateStatus(Status.saving),
		onSuccess: (data) => {
			queryClient.setQueryData(getCzMaxPriceQueryKey(groupId), data);
			setUpdateStatus(Status.saved);
		},
		onError: () => setUpdateStatus(Status.failed),
	});

	const mutationRef = useRef(mutation);
	mutationRef.current = mutation;

	function scheduleSave(packageSize: number | null, basketVersion: string, products: ProductUpdate[]) {
		if (timeoutRef.current !== null) {
			clearTimeout(timeoutRef.current);
		}
		scheduledRef.current = { packageSize, basketVersion, products };
		setUpdateStatus(Status.saving);

		timeoutRef.current = setTimeout(() => {
			timeoutRef.current = null;
			scheduledRef.current = null;
			mutationRef.current.mutate({ packageSize, basketVersion, products });
		}, 2000);
	}

	async function flushSave() {
		if (timeoutRef.current !== null) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		const scheduled = scheduledRef.current;
		if (scheduled !== null) {
			scheduledRef.current = null;
			await mutationRef.current.mutateAsync(scheduled);
		}
	}

	const exportMutationRaw = useMutation({
		mutationFn: () => postCzMaxPriceExportXls(userId!, groupId),
	});
	const exportMutationRef = useRef(exportMutationRaw);
	exportMutationRef.current = exportMutationRaw;

	async function triggerExport() {
		try {
			const response = await exportMutationRef.current.mutateAsync();
			if (response.export.downloadUrl !== null) {
				downloadFileFromUrl(response.export.downloadUrl);
			}
		} catch (error: any) {
			const errors: Array<{ code: string }> = error?.response?.data?.errors ?? [];
			const codes = errors.map((e) => e.code);

			if (codes.includes('czech-max-price-calculation-requires-reference-settings')) {
				dispatch(flashErrorMessage(downloadFlashMessages.MissingPackageSizeErrorMessage));
			} else if (codes.includes('czech-max-price-calculation-required-product-settings-is-missing')) {
				dispatch(flashErrorMessage(downloadFlashMessages.MissingProductSettingsErrorMessage));
			} else if (codes.includes('czech-max-price-calculation-requires-at-least-one-reference-country-product')) {
				dispatch(flashErrorMessage(downloadFlashMessages.MissingReferenceCountryProductErrorMessage));
			} else if (codes.includes('czech-max-price-calculation-requires-at-most-one-product-for-each-country')) {
				dispatch(flashErrorMessage(downloadFlashMessages.OnlyOneProductPerCountryErrorMessage));
			} else if (codes.includes('czech-max-price-calculation-illegal-price-type-selected')) {
				dispatch(flashErrorMessage(downloadFlashMessages.IllegalPriceTypeSelectedErrorMessage));
			} else if (codes.includes('czech-max-price-calculation-requires-at-least-three-reference-country-products')) {
				dispatch(flashErrorMessage(downloadFlashMessages.MissingThreeReferenceCountryProductsMessage));
			} else {
				dispatch(handleError(error));
			}
		}
	}

	async function deleteProducts(group: UserProductGroup, products: GeneralProduct[]) {
		try {
			await dispatch(removeFromGroup(group.id, products));
			logGoogleTagManagerGroupProductsRemove(group.id, products);
		} catch (error: unknown) {
			dispatch(handleError(error));
		}
	}

	async function exportProductsXml(groupIdValue: string, currency: string) {
		try {
			logGoogleTagManagerGroupExport(groupIdValue);
			const response: ExportResult | null = await dispatch(createGroupExportXmlUrl(groupIdValue, currency));
			if (response?.export) {
				const { processExport } = await import('~/ProductGroupDownloadPopover/productGroupDownloadHelpers');
				dispatch(processExport(response.export));
			}
		} catch (error: unknown) {
			dispatch(handleError(error));
		}
	}

	async function exportEvidence(groupIdValue: string) {
		try {
			const response: ExportResult | null = await dispatch(createEvidenceExport(groupIdValue));
			if (response?.export) {
				const { processExport } = await import('~/ProductGroupDownloadPopover/productGroupDownloadHelpers');
				dispatch(processExport(response.export));
			}
		} catch (error: unknown) {
			dispatch(handleError(error));
		}
	}

	function navigateAfterDelete(group: UserProductGroup) {
		history.push(`/product-groups/${group.parent !== null ? group.parent.id : ''}`);
	}

	return {
		czMaxPrice,
		isFetching,
		basketVersions,
		scheduleSave,
		flushSave,
		updateStatus,
		triggerExport,
		deleteProducts,
		exportProductsXml,
		exportEvidence,
		navigateAfterDelete,
	};
}
